import { Avatar, AvatarGroup, Box, Chip, Skeleton, Stack, styled, Typography } from "@mui/material";
import { ITouchpoint } from "../../../../types";
import { DisplayBoxBase, TagChip } from "../../../../components/shared/StyledComponents";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { VideoJS } from "../../../../components/VideoJs";
import useAnalytics from "../../../../hooks/useAnalytics";


const TouchpointHeader = styled(Typography)(({ theme }) => ({
    fontSize: "1.2rem",
    fontWeight: "bold",
    lineHeight: "1"
}));

const TouchpointDate = styled(Typography)(({ theme }) => ({
    lineHeight: "1"
}));

export const InsightsItem = ({ touchpoint }: { touchpoint: ITouchpoint }) => {
    const { trackEvent } = useAnalytics();

    const handleVideoPlay = () => {
        trackEvent("Watched highlight video", {
            project: touchpoint.project.tag,
            organization: touchpoint.project.organization.tag,
            touchpoint_uuid: touchpoint.uuid,
            touchpoint_name: touchpoint.name,
            video_url: touchpoint.highlight_video_url,
        });
    }

    return (
        <DisplayBoxBase
            className={touchpoint.status === "active" ? "gray" : "white"}
            sx={{
                gap: 2,
                justifyContent: "space-between",
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                flexWrap: "wrap",
            }}
        >
            <Stack direction="column" gap={1} sx={{ flex: 1 }} justifyContent="space-between">
                {touchpoint.status !== "active" &&
                    <Box>
                        <TouchpointDate variant="caption">
                            {dayjs(touchpoint.started_at).format("MMM DD, YYYY")}{touchpoint.completed_at ? ` - ${dayjs(touchpoint.completed_at).format("MMM DD, YYYY")}` : ""}
                        </TouchpointDate>
                    </Box>
                }
                <TouchpointHeader><Link to={`/c/insights/${touchpoint.uuid}`}>{touchpoint.name}</Link></TouchpointHeader>
                {/* Custom avatar row with overflow label */}
                <Box display="flex" alignItems="center" sx={{ padding: "0.2rem 0" }}>
                    <AvatarGroup max={6}>
                        {touchpoint.viewpoints?.slice(0, 6).map((viewpoint) => (
                            <Avatar
                                key={viewpoint.advisor?.uuid}
                                src={viewpoint.advisor?.user?.profile?.image_url}
                                sx={{ width: 24, height: 24 }}
                            />
                        ))}
                        {!touchpoint.viewpoints?.length && (
                            <Skeleton variant="circular" width={24} height={24} />
                        )}
                    </AvatarGroup>
                    {touchpoint.viewpoints && touchpoint.viewpoints.length > 6 && (
                        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                            +{touchpoint.viewpoints.length - 6} advisors
                        </Typography>
                    )}
                    {touchpoint.viewpoints?.length === 0 && (
                        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                            Collecting responses...
                        </Typography>
                    )}
                </Box>
                {touchpoint.status === "active" && touchpoint.media_analysis_summary && (
                    <Box
                        dangerouslySetInnerHTML={{
                            __html: touchpoint.media_analysis_summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                    />
                )}
                { touchpoint.media_analysis_topics && touchpoint.media_analysis_topics.length > 0 && (
                <Stack direction="row" gap={1} sx={{ padding: "0.2rem 0", mt: 'auto', flexWrap: "wrap" }}>
                    {touchpoint.media_analysis_topics && touchpoint.media_analysis_topics.slice(0, 3).map((topic) => (
                        <TagChip key={topic} variant="outlined" label={topic} />
                    ))}
                </Stack>
                )}
            </Stack>
            {touchpoint.status === "active" && touchpoint.highlight_video_url ? (
                <Box sx={{ height: "100%", width: "416px" }}>
                    {touchpoint.highlight_video_url ? (
                        <VideoJS
                            src={touchpoint.highlight_video_url}
                            startAt={0.5}
                            onPlay={handleVideoPlay}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 416,
                                height: 234,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "grey.100",
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "grey.300"
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Highlight reel coming soon
                            </Typography>
                        </Box>
                    )}
                </Box>
            ) : touchpoint?.viewpoints && touchpoint?.viewpoints?.length > 0 ? (
                <Box sx={{ height: "100%", width: "208px" }}>
                    {touchpoint.highlight_video_url ? (
                        <VideoJS
                            src={touchpoint.highlight_video_url}
                            startAt={0.5}
                            onPlay={handleVideoPlay}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 208,
                                height: 117,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "grey.100",
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "grey.300"
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                                Highlight reel coming soon
                            </Typography>
                        </Box>
                    )}
                </Box>
            ) : (
                <></>
            )}
        </DisplayBoxBase>
    );
};